import React, { useCallback, useEffect, useState } from 'react';
import api from '../api/axiosConfig';
import CheckoutModal from './CheckoutModal';
import { formatMoney, usePricing, yearlySavingPercent } from '../hooks/usePricing';
import { useLanguage } from '../i18n/LanguageContext';
import './SubscriptionSection.css';

// Settings > Subscription: current plan, usage vs free limits, upgrade
// and cancel. Data comes from /billing/status/.

interface Usage {
    used: number;
    limit: number | null;
}

interface BillingStatus {
    premium: boolean;
    plan: string;
    status: string;
    current_period_end: string | null;
    usage: { notes: Usage; documents: Usage; storyboards: Usage; storage_bytes: Usage };
}

const formatBytes = (n: number) => {
    if (n >= 1024 ** 3) return `${(n / 1024 ** 3).toFixed(1)} GB`;
    if (n >= 1024 ** 2) return `${(n / 1024 ** 2).toFixed(0)} MB`;
    return `${Math.max(1, Math.round(n / 1024))} KB`;
};

const UsageBar: React.FC<{ label: string; usage: Usage; bytes?: boolean }> = ({ label, usage, bytes }) => {
    const { t } = useLanguage();
    const pct = usage.limit ? Math.min(100, (usage.used / usage.limit) * 100) : 0;
    return (
        <div className="usage-row">
            <div className="usage-label">
                <span>{label}</span>
                <span>
                    {bytes ? formatBytes(usage.used) : usage.used}
                    {usage.limit != null && ` / ${bytes ? formatBytes(usage.limit) : usage.limit}`}
                    {usage.limit == null && ` · ${t.billing.unlimited}`}
                </span>
            </div>
            {usage.limit != null && (
                <div className="usage-track">
                    <div
                        className={`usage-fill ${pct >= 90 ? 'usage-hot' : ''}`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
            )}
        </div>
    );
};

export const SubscriptionSection: React.FC = () => {
    const { t, language } = useLanguage();
    const [billing, setBilling] = useState<BillingStatus | null>(null);
    const [checkoutPlan, setCheckoutPlan] = useState<'monthly' | 'yearly' | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isCanceling, setIsCanceling] = useState(false);
    const { pricing } = usePricing();
    // The currency symbol sits with the amount rather than in the translated
    // string, so where it goes can move with the language later on.
    const monthlyPrice = formatMoney(pricing.monthly, pricing.currency, language);
    const yearlyPrice = formatMoney(pricing.yearly, pricing.currency, language);
    const saving = yearlySavingPercent(pricing);

    // Prices are banded by country, so the plan label is built from the
    // resolved pricing rather than baked in here.
    const planName = (plan: string): string => ({
        free: t.billing.planFree,
        monthly: t.billing.planMonthly.replace('{price}', monthlyPrice),
        yearly: t.billing.planYearly.replace('{price}', yearlyPrice),
        comp: t.billing.planComp,
    }[plan] || plan);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get('/billing/status/');
            setBilling(data);
        } catch {
            setError(t.billing.loadFailed);
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const handleCancel = async () => {
        if (!window.confirm(t.billing.cancelConfirm)) return;
        setIsCanceling(true);
        setError(null);
        try {
            await api.post('/billing/cancel/', {});
            await load();
        } catch (e: any) {
            setError(e?.response?.data?.error || t.billing.cancelFailed);
        } finally {
            setIsCanceling(false);
        }
    };

    if (!billing) {
        return <p className="placeholder-text">{error || t.billing.loading}</p>;
    }

    const paidActive = billing.premium && (billing.plan === 'monthly' || billing.plan === 'yearly')
        && billing.status === 'active';

    return (
        <div className="subscription-section">
            <div className="subscription-plan-row">
                <div>
                    <div className="subscription-plan-name">
                        {planName(billing.plan)}
                        {billing.premium && <span className="premium-badge">{t.billing.premiumBadge}</span>}
                    </div>
                    {billing.status === 'past_due' && (
                        <div className="subscription-warning">
                            {t.billing.pastDueWarning.replace(
                                '{date}',
                                billing.current_period_end
                                    ? new Date(billing.current_period_end).toLocaleDateString()
                                    : ''
                            )}
                        </div>
                    )}
                    {billing.status === 'canceled' && billing.premium && (
                        <div className="subscription-warning">
                            {t.billing.canceledWarning.replace(
                                '{date}',
                                billing.current_period_end
                                    ? new Date(billing.current_period_end).toLocaleDateString()
                                    : ''
                            )}
                        </div>
                    )}
                </div>
                {paidActive && (
                    <div className="subscription-manage-btns">
                        <button
                            className="subscription-switch-btn"
                            onClick={() => {
                                const other = billing.plan === 'monthly' ? 'yearly' : 'monthly';
                                if (window.confirm(
                                    other === 'yearly'
                                        ? t.billing.switchToYearlyConfirm
                                            .replace('{price}', yearlyPrice)
                                            .replace('{saving}', String(saving))
                                        : t.billing.switchToMonthlyConfirm.replace('{price}', monthlyPrice)
                                )) {
                                    setCheckoutPlan(other);
                                }
                            }}
                        >
                            {billing.plan === 'monthly'
                                ? t.billing.switchToYearly.replace('{saving}', String(saving))
                                : t.billing.switchToMonthly}
                        </button>
                        <button
                            className="subscription-cancel-btn"
                            onClick={handleCancel}
                            disabled={isCanceling}
                        >
                            {isCanceling ? t.billing.canceling : t.billing.cancelSubscription}
                        </button>
                    </div>
                )}
            </div>

            {error && <div className="subscription-error">{error}</div>}

            <UsageBar label={t.billing.usageNotes} usage={billing.usage.notes} />
            <UsageBar label={t.billing.usageDocuments} usage={billing.usage.documents} />
            <UsageBar label={t.billing.usageStoryboards} usage={billing.usage.storyboards} />
            <UsageBar label={t.billing.usageStorage} usage={billing.usage.storage_bytes} bytes />

            {!billing.premium && (
                <div className="subscription-upgrade-row">
                    <button className="upgrade-btn" onClick={() => setCheckoutPlan('monthly')}>
                        {t.billing.upgradeMonthly.replace('{price}', monthlyPrice)}
                    </button>
                    <button className="upgrade-btn featured" onClick={() => setCheckoutPlan('yearly')}>
                        {t.billing.upgradeYearly
                            .replace('{price}', yearlyPrice)
                            .replace('{saving}', String(saving))}
                    </button>
                </div>
            )}

            {checkoutPlan && (
                <CheckoutModal
                    plan={checkoutPlan}
                    onClose={() => { setCheckoutPlan(null); load(); }}
                    onSuccess={() => load()}
                />
            )}
        </div>
    );
};

export default SubscriptionSection;
